import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Phone, Search } from "lucide-react";

import {
  BG_PROMO,
  BORDER,
  FOOTER_DARK,
  INK,
  LINK_BLUE,
  SUB,
  TEAL,
  TEAL_DARK,
  linkStyle,
  olStyle,
  ulStyle,
} from "./styles";
import {
  CONTENT_MAX,
  FOOTER_LOCALES,
  MENUS,
  NAV_LOCALES,
  RELATED_LINKS,
} from "./constants";
import LocaleIcon from "./components/LocaleIcon";
import LocaleRow from "./components/LocaleRow";
import NavMenu from "./components/NavMenu";
import Section from "./components/Section";
import FooterCol from "./components/FooterCol";

export default function HelpAutoPayroll() {
  const [locale, setLocale] = useState("en-CA");
  const [showNavLocale, setShowNavLocale] = useState(false);
  const [showFooterLocale, setShowFooterLocale] = useState(false);
  const [search, setSearch] = useState("");
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [hoverSupport, setHoverSupport] = useState(false);
  const navigate = useNavigate();
  const navLocaleRef = useRef<HTMLDivElement | null>(null);
  const footerLocaleRef = useRef<HTMLDivElement | null>(null);

  const currentNavLocale =
    NAV_LOCALES.find((l) => l.code === locale) || NAV_LOCALES[1];
  const currentFooterLocale =
    FOOTER_LOCALES.find((l) => l.code === locale) || FOOTER_LOCALES[2];

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        navLocaleRef.current &&
        !navLocaleRef.current.contains(e.target as Node)
      ) {
        setShowNavLocale(false);
      }
      if (
        footerLocaleRef.current &&
        !footerLocaleRef.current.contains(e.target as Node)
      ) {
        setShowFooterLocale(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectLocale = (code: string) => {
    setLocale(code);
    setShowNavLocale(false);
    setShowFooterLocale(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = search.trim();
    if (!q) return;
    navigate("/help/search?q=" + encodeURIComponent(q));
  };

  const supportLinkStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 700,
    color: INK,
    textDecoration: "none",
    borderBottom: hoverSupport
      ? "2px solid " + TEAL
      : "2px solid transparent",
    paddingBottom: 4,
    transition: "border-color 0.15s",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#fff",
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
        color: INK,
      }}
    >
      <div style={{ borderBottom: "1px solid " + BORDER, padding: "12px 32px" }}>
        <div
          style={{
            maxWidth: CONTENT_MAX,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 14,
          }}
        >
          <a href="/" style={{ textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
            <span
              style={{
                color: "#0F9599",
                fontWeight: 800,
                fontSize: 24,
                letterSpacing: "-0.015em",
                fontFamily: "inherit",
              }}
            >
              Novala
            </span>
          </a>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <a href="/pricing" style={{ color: INK, textDecoration: "none", fontWeight: 500 }}>
              Plans & Pricing
            </a>
            <a href="/support" style={{ color: INK, textDecoration: "none", fontWeight: 500 }}>
              Support
            </a>
            <span style={{ color: SUB, display: "flex", alignItems: "center", gap: 6 }}>
              <Phone size={14} strokeWidth={2} />
              <span>1-800-NOVALA</span>
            </span>
            <a
              href="/login"
              style={{
                padding: "8px 18px",
                borderRadius: 8,
                background: "#fff",
                color: LINK_BLUE,
                border: "1.5px solid " + LINK_BLUE,
                fontWeight: 600,
                fontSize: 14,
                textDecoration: "none",
              }}
            >
              Sign In
            </a>
          </div>
        </div>
      </div>

      <div style={{ padding: "16px 32px", position: "sticky", top: 0, zIndex: 40, background: "#fff" }}>
        <div
          style={{
            maxWidth: CONTENT_MAX,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <a
              href="#"
              onMouseEnter={() => setHoverSupport(true)}
              onMouseLeave={() => setHoverSupport(false)}
              style={supportLinkStyle}
            >
              Novala Support
            </a>
            {Object.entries(MENUS).map(([name, items]) => (
              <NavMenu
                key={name}
                label={name}
                items={items}
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
              />
            ))}
          </div>
          <div ref={navLocaleRef} style={{ position: "relative" }}>
            <button
              onClick={() => setShowNavLocale((s) => !s)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: "transparent",
                border: "none",
                padding: "4px 0 4px 6px",
                borderRadius: 4,
                cursor: "pointer",
                fontFamily: "inherit",
                color: INK,
              }}
            >
              <LocaleIcon locale={currentNavLocale} />
              <ChevronDown size={14} strokeWidth={2} />
            </button>
            {showNavLocale && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  right: 0,
                  background: "#fff",
                  border: "1px solid " + BORDER,
                  borderRadius: 10,
                  minWidth: 240,
                  boxShadow: "0 10px 30px -10px rgba(0,0,0,0.18)",
                  zIndex: 50,
                  overflow: "hidden",
                }}
              >
                {NAV_LOCALES.map((l) => (
                  <LocaleRow
                    key={l.code}
                    locale={l}
                    isSelected={l.code === locale}
                    onClick={() => selectLocale(l.code)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ background: BG_PROMO, padding: "20px 32px" }}>
        <div
          style={{
            maxWidth: CONTENT_MAX,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
            fontSize: 15,
          }}
        >
          <span style={{ color: INK }}>
            Explore our Novala training videos and learn at your own pace
          </span>
          <button
            style={{
              padding: "10px 22px",
              borderRadius: 8,
              background: TEAL,
              color: "#fff",
              border: "none",
              fontWeight: 700,
              fontSize: 14,
              cursor: "pointer",
              fontFamily: "inherit",
              flexShrink: 0,
              boxShadow: "0 6px 14px -6px rgba(15,149,153,0.6)",
            }}
          >
            Master Novala today
          </button>
        </div>
      </div>

      <div
        style={{
          background: "linear-gradient(135deg, " + TEAL + " 0%, " + TEAL_DARK + " 100%)",
          padding: "56px 32px",
        }}
      >
        <div style={{ maxWidth: CONTENT_MAX, margin: "0 auto" }}>
          <form
            onSubmit={handleSearch}
            style={{
              display: "flex",
              width: "100%",
              maxWidth: 560,
              background: "#fff",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 10px 30px -10px rgba(0,0,0,0.25)",
            }}
          >
            <input
              type="text"
              name="q"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions, keywords or topics"
              style={{
                flex: 1,
                padding: "14px 18px",
                border: "none",
                outline: "none",
                fontSize: 15,
                fontFamily: "inherit",
                color: INK,
              }}
            />
            <button
              type="submit"
              style={{
                padding: "0 24px",
                background: TEAL,
                color: "#fff",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Search size={20} strokeWidth={2} />
            </button>
          </form>
        </div>
      </div>

      <div
        style={{
          maxWidth: CONTENT_MAX,
          margin: "0 auto",
          padding: "48px 32px 80px",
          display: "grid",
          gridTemplateColumns: "1fr 280px",
          gap: 60,
        }}
      >
        <article>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: TEAL,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 800,
                fontSize: 13,
              }}
            >
              N
            </div>
            <span style={{ color: SUB, fontSize: 13, fontWeight: 600 }}>Novala Help</span>
          </div>
          <h1 style={{ margin: "0 0 12px 0", fontSize: 32, fontWeight: 700, letterSpacing: "-0.01em" }}>
            Set up and manage your Auto Payroll
          </h1>
          <div style={{ color: SUB, fontSize: 14, marginBottom: 28 }}>
            by Novala · Updated a few days ago
          </div>
          <p style={{ fontSize: 16, lineHeight: 1.65, color: INK, marginBottom: 18 }}>
            Novala Payroll saves time by automatically creating pay (cheques or direct deposits) for salaried and hourly employees who work a set number of hours. Auto Payroll works for employees paid by direct deposit or paper cheque.
          </p>
          <p style={{ fontSize: 15, color: INK, marginBottom: 8, fontWeight: 600 }}>
            We'll show you how to:
          </p>
          <ul style={{ margin: "0 0 32px 0", paddingLeft: 22, lineHeight: 1.9 }}>
            <li><a href="#need" style={linkStyle}>What you'll need</a></li>
            <li><a href="#one-time" style={linkStyle}>Make a one-time change to an upcoming auto paycheque</a></li>
            <li><a href="#manual" style={linkStyle}>Pay an employee manually for one pay period</a></li>
          </ul>

          <Section id="need" title="What you'll need">
            <p>Before setting up Auto Payroll, finish initial payroll setup and run your first payroll.</p>
            <p>Employees must be eligible:</p>
            <ul style={ulStyle}>
              <li>Have an <strong>active</strong> status.</li>
              <li>Be paid a <strong>fixed salary</strong> OR be <strong>hourly with default hours set</strong>.</li>
              <li>Be <strong>assigned to a pay schedule</strong>.</li>
              <li>Have <strong>complete TD1 tax info</strong> in their profile.</li>
            </ul>
            <p>Auto Payroll is <strong>not</strong> available for commission-only, inactive, or hourly employees with varying hours each pay period.</p>
          </Section>

          <Section title="Set default hours for hourly employees">
            <p>For hourly employees who work the same hours each day, set default hours to make them eligible:</p>
            <ol style={olStyle}>
              <li>Go to <strong>All Apps</strong>, select <strong>Payroll</strong>, then select <strong>Employees</strong>. <a href="#" style={linkStyle}>(Take me there)</a></li>
              <li>Select the employee.</li>
              <li>From <strong>Base pay</strong>, select <strong>Start</strong> or <strong>Edit</strong>.</li>
              <li>Enter <strong>Hours per day</strong> and <strong>Days per week</strong>.</li>
              <li>Select <strong>Save</strong>.</li>
            </ol>
          </Section>

          <Section title="Add or remove eligible employees from Auto Payroll">
            <p style={{ fontStyle: "italic", color: SUB }}>(Must be the Primary Admin to manage Auto Payroll settings.)</p>
            <ol style={olStyle}>
              <li>Go to <strong>Settings</strong>, then <strong>Payroll settings</strong>.</li>
              <li>In the <strong>Auto Payroll</strong> section, select the pencil icon.</li>
              <li>Choose the employees to enroll, then <strong>Save changes</strong>.</li>
              <li>Review the confirmation, then select <strong>Done</strong>.</li>
            </ol>
            <p style={{ background: "#F1F5F5", padding: "12px 16px", borderRadius: 8, fontSize: 14, marginTop: 14 }}>
              Once enrolled, you'll get an email <strong>2 business days before</strong> Auto Payroll processes, and a confirmation email <strong>after</strong>.
            </p>
          </Section>

          <Section id="one-time" title="Make a one-time change to an upcoming auto paycheque">
            <p>Changes must be made <strong>3 business days before the cheque date, by 8 PM ET</strong>.</p>
            <ol style={olStyle}>
              <li>Go to <strong>All Apps</strong>, select <strong>Payroll</strong>, then select <strong>Employees</strong>. <a href="#" style={linkStyle}>(Take me there)</a></li>
              <li>Select <strong>Run payroll</strong>.<div style={{ color: SUB, fontSize: 14, marginTop: 4 }}><strong>Note:</strong> If there are multiple pay schedules, pick one and select <strong>Continue</strong>.</div></li>
              <li>Select <strong>Make updates to upcoming pay period</strong>.</li>
              <li>In the <strong>Actions</strong> column, open the more menu, then <strong>Edit paycheque</strong>.</li>
              <li>Edit the items, then <strong>Save</strong>.</li>
              <li>Select <strong>Save and preview Auto Payroll</strong>, then <strong>Save Auto Payroll</strong>.</li>
            </ol>
          </Section>

          <Section id="manual" title="Pay an employee manually for one pay period">
            <p>Pauses Auto Payroll for a single period; it resumes the next scheduled run.</p>
            <ol style={olStyle}>
              <li>Go to <strong>All Apps</strong>, select <strong>Payroll</strong>, then select <strong>Employees</strong>. <a href="#" style={linkStyle}>(Take me there)</a></li>
              <li>Select <strong>Run payroll</strong>.<div style={{ color: SUB, fontSize: 14, marginTop: 4 }}><strong>Note:</strong> If there are multiple pay schedules, pick one and select <strong>Continue</strong>.</div></li>
              <li>Select <strong>Pay manually this period</strong> on the relevant employees.</li>
              <li>Enter a <strong>Pay date</strong> and edit any pay items.</li>
              <li>Select <strong>Preview payroll</strong>, pick an account, then <strong>Submit payroll</strong>.</li>
            </ol>
          </Section>

          <Section title="Results">
            <p>Once Auto Payroll is set up, you'll get an email two business days before payday with the upcoming pay-run details, and a confirmation email after it processes.</p>
          </Section>

          <Section title="Related links">
            <ul style={{ ...ulStyle, lineHeight: 2 }}>
              <li><a href="#" style={linkStyle}>Get started with Novala Payroll</a></li>
              <li><a href="#" style={linkStyle}>Add a new employee to Novala Payroll</a></li>
              <li><a href="#" style={linkStyle}>Set up and manage payroll schedules</a></li>
            </ul>
          </Section>
        </article>

        <aside>
          <h3 style={{ margin: "0 0 16px 0", fontSize: 15, fontWeight: 700, color: INK }}>
            More like this
          </h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {RELATED_LINKS.map((link, i) => (
              <div key={i}>
                <a href="#" style={{ ...linkStyle, fontSize: 14, display: "block", marginBottom: 4 }}>
                  {link}
                </a>
                <div style={{ fontSize: 12, color: SUB }}>by Novala</div>
              </div>
            ))}
          </div>
        </aside>
      </div>

      <footer style={{ background: FOOTER_DARK, color: "#fff", padding: "48px 32px 32px" }}>
        <div style={{ maxWidth: CONTENT_MAX, margin: "0 auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 40,
              marginBottom: 32,
            }}
          >
            <FooterCol title="Product" items={["Features", "Pricing", "Security", "Integrations"]} />
            <FooterCol title="Company" items={["About", "Blog", "Careers", "Contact"]} />
            <FooterCol title="Resources" items={["Help Center", "Community", "Training", "Status"]} />
            <FooterCol title="Legal" items={["Privacy", "Terms", "Cookies"]} />
          </div>
          <div
            style={{
              borderTop: "1px solid rgba(255,255,255,0.15)",
              paddingTop: 24,
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 16,
              fontSize: 13,
              color: "rgba(255,255,255,0.78)",
            }}
          >
            <div>© 2026 Novala · BrightCare Home Healthcare Services Inc.</div>
            <div ref={footerLocaleRef} style={{ position: "relative" }}>
              <button
                onClick={() => setShowFooterLocale((s) => !s)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: "transparent",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.3)",
                  padding: "6px 12px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 13,
                }}
              >
                <LocaleIcon locale={currentFooterLocale} />
                <span>Select a Country</span>
                <ChevronDown size={14} strokeWidth={2} />
              </button>
              {showFooterLocale && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "calc(100% + 6px)",
                    right: 0,
                    background: "#fff",
                    border: "1px solid " + BORDER,
                    borderRadius: 10,
                    minWidth: 240,
                    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.25)",
                    zIndex: 50,
                    overflow: "hidden",
                  }}
                >
                  {FOOTER_LOCALES.map((l) => (
                    <LocaleRow
                      key={l.code}
                      locale={l}
                      isSelected={l.code === locale}
                      onClick={() => selectLocale(l.code)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}